import type { OutreachStatus, ProspectStatus, Prospect } from '../api/prospectsApi'

// The outreach funnel, in order. Colour tracks progression, with the two terminal states
// (bounced, unsubscribed) deliberately NOT red-vs-green on a good/bad axis: an unsubscribe
// is a person exercising a choice, not a failure, and styling it as an error trains
// reviewers to treat honoured consent as something to fix.
const OUTREACH_STYLES: Record<OutreachStatus, { label: string; className: string }> = {
  none: { label: 'Not contacted', className: 'bg-slate-100 text-slate-600' },
  queued: { label: 'Queued', className: 'bg-slate-100 text-slate-700' },
  contacted: { label: 'Contacted', className: 'bg-blue-100 text-blue-700' },
  opened: { label: 'Opened', className: 'bg-sky-100 text-sky-700' },
  replied: { label: 'Replied', className: 'bg-violet-100 text-violet-700' },
  interested: { label: 'Interested', className: 'bg-emerald-100 text-emerald-700' },
  not_interested: { label: 'Not interested', className: 'bg-amber-100 text-amber-800' },
  converted: { label: 'Converted', className: 'bg-emerald-600 text-white' },
  bounced: { label: 'Bounced', className: 'bg-orange-100 text-orange-800' },
  unsubscribed: { label: 'Unsubscribed', className: 'bg-slate-700 text-white' },
}

export function OutreachBadge({ status }: { status: OutreachStatus }) {
  const s = OUTREACH_STYLES[status] ?? OUTREACH_STYLES.none
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.className}`}>
      {s.label}
    </span>
  )
}

const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  candidate: 'Candidate',
  enriched: 'Enriched',
  generated: 'Generated',
  review: 'In review',
  published: 'Published',
  claimed: 'Claimed',
  suppressed: 'Suppressed',
}

export function StatusBadge({ status }: { status: ProspectStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        status === 'suppressed' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'
      }`}
    >
      {PROSPECT_STATUS_LABELS[status] ?? status}
    </span>
  )
}

/**
 * The two suppressions, shown SEPARATELY and never merged into one "blocked" chip.
 *
 * `status === 'suppressed'` is a publishing takedown. `doNotContact` is contact consent.
 * Someone may want the page and refuse the email, or the reverse. Collapsing them in the
 * UI is how a reviewer ends up honouring the wrong one — and both are terminal, so the
 * mistake is not correctable afterwards.
 */
export function ConsentFlags({ prospect }: { prospect: Prospect }) {
  const publishingBlocked = prospect.status === 'suppressed'
  if (!publishingBlocked && !prospect.doNotContact) return null

  return (
    <span className="inline-flex gap-1">
      {publishingBlocked && (
        <span
          className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
          title={prospect.suppressedReason || 'Publishing takedown'}
        >
          No page
        </span>
      )}
      {prospect.doNotContact && (
        <span
          className="inline-flex items-center rounded-full bg-slate-700 px-2 py-0.5 text-xs font-medium text-white"
          title={prospect.doNotContactReason || 'Contact consent withdrawn'}
        >
          Do not contact
        </span>
      )}
    </span>
  )
}

/**
 * Confidence in a discovered address, which is NOT uniform: an address in the artist's own
 * bio is near-certain, one crawled off a page that merely mentions them is a guess. Shown
 * because sending to a low-confidence address costs sender reputation, and the number is
 * recorded but has never been validated against real deliverability.
 */
export function EmailConfidence({ prospect }: { prospect: Prospect }) {
  if (!prospect.email) return <span className="text-xs text-slate-400">—</span>
  // Coerced even though the API now sends a number and the type says so. A render-time throw
  // here does not degrade one row, it unmounts the route and shows a blank error page — the cost
  // of being wrong is the whole screen, so this does not rely on the wire being right.
  const raw = Number(prospect.emailConfidence ?? 0)
  const c = Number.isFinite(raw) ? raw : 0
  const tone = c >= 0.8 ? 'text-emerald-700' : c >= 0.55 ? 'text-amber-700' : 'text-orange-700'
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-xs">{prospect.email}</span>
      <span className={`text-xs font-medium ${tone}`} title={`source: ${prospect.emailSource ?? 'unknown'}`}>
        {c ? c.toFixed(2) : '?'}
      </span>
    </span>
  )
}
